import {
  AutocompleteOptionForHookForm,
  StrictAutocompleteOptionForHookForm,
} from '../Autocomplete';
import { InternalService } from '../InternalService';
import { SparePartCatalogItemWithPrice } from '../SparePartCatalog';
import { SparePartSupplier } from '../SparePartSupplier';

import { RecipientType } from './RecipientType';

export const QUOTATION_LINE_TYPES = {
  UNKNOWN_SERVICE: 'unknownService',
  KNOWN_AND_UNKNOWN_SERVICE: 'knownAndUnknownService',
  DISCOUNT: 'discount',
  ADMINISTRATIVE_FEES: 'administrativeFees',
  SPARE_PARTS: 'spareParts',
  UNKNOWN_SPARE_PART: 'unknownSparePart',
} as const;

export type QuotationLineTypes =
  (typeof QUOTATION_LINE_TYPES)[keyof typeof QUOTATION_LINE_TYPES];

export const LINE_TYPE_OPTIONS = {
  UNKNOWN_SERVICE: 'unknownService',
  KNOWN_AND_UNKNOWN_SERVICE: 'knownAndUnknownService',
  DISCOUNT: 'discount',
  UNKNOWN_SPARE_PART: 'unknownSparePart',
  KNOWN_AND_UNKNOWN_SPARE_PART: 'knownAndUnknownSparePart',
  STOCK_ITEM: 'stockItem',
  ADMINISTRATIVE_FEES: 'administrativeFees',
} as const;

export type LineTypeOptions =
  (typeof LINE_TYPE_OPTIONS)[keyof typeof LINE_TYPE_OPTIONS];

export const QUOTE_LINE_FIELDS = {
  LINE_TYPE: 'lineType',
  LINE: 'line',

  SERVICE_DESIGNATION: 'serviceDesignation',
  SERVICE_PRICE_EXCLUDED_VAT: 'servicePriceExcludedVat',
  SERVICE_PRICE_INCLUDED_VAT: 'servicePriceIncludedVat',

  SPARE_PART_DESIGNATION: 'sparePartDesignation',
  SPARE_PART_REF: 'sparePartRef',
  SPARE_PART_BUYING_PRICE_EXCLUDED_VAT: 'sparePartBuyingPriceExcludedVat',
  SPARE_PART_SELLING_PRICE_EXCLUDED_VAT: 'sparePartSellingPriceExcludedVat',
  SPARE_PART_SELLING_PRICE_INCLUDED_VAT: 'sparePartSellingPriceIncludedVat',
  SPARE_PART_SUPPLIER: 'sparePartSupplier',

  UNKNOWN_SPARE_PART_REF: 'unknownSparePartRef',
  UNKNOWN_SPARE_PART_DESIGNATION: 'unknownSparePartDesignation',
  UNKNOWN_SPARE_PART_BUYING_PRICE_EXCLUDED_VAT:
    'unknownSparePartBuyingPriceExcludedVat',
  UNKNOWN_SPARE_PART_SELLING_PRICE_EXCLUDED_VAT:
    'unknownSparePartSellingPriceExcludedVat',
  UNKNOWN_SPARE_PART_SELLING_PRICE_INCLUDED_VAT:
    'unknownSparePartSellingPriceIncludedVat',

  UNKNOWN_SERVICE_DESIGNATION: 'unknownServiceDesignation',
  UNKNOWN_SERVICE_PRICE_EXCLUDED_VAT: 'unknownServicePriceExcludedVat',
  UNKNOWN_SERVICE_PRICE_INCLUDED_VAT: 'unknownServicePriceIncludedVat',

  DISCOUNT_DESIGNATION: 'discountDesignation',
  DISCOUNT_PRICE: 'discountPrice',

  ADMINISTRATIVE_FEES_DESIGNATION: 'administrativeFeesDesignation',
  ADMINISTRATIVE_FEES_PRICE_EXCLUDED_VAT: 'administrativeFeesPriceExcludedVat',
  ADMINISTRATIVE_FEES_PRICE_INCLUDED_VAT: 'administrativeFeesPriceIncludedVat',

  STOCK_ITEM_REF: 'stockItemRef',
  STOCK_ITEM_ID: 'stockItemId',
  STOCK_ITEM_SUPPLIER: 'stockItemSupplier',
  STOCK_ITEM_DESIGNATION: 'stockItemDesignation',
  STOCK_ITEM_BUYING_PRICE_EXCLUDED_VAT: 'stockItemBuyingPriceExcludedVat',
  STOCK_ITEM_SELLING_PRICE_EXCLUDED_VAT: 'stockItemSellingPriceExcludedVat',
  STOCK_ITEM_SELLING_PRICE_INCLUDED_VAT: 'stockItemSellingPriceIncludedVat',
} as const;

export type QuoteLineFields =
  (typeof QUOTE_LINE_FIELDS)[keyof typeof QUOTE_LINE_FIELDS];

export const SPARE_PART_LINE_TYPES: LineTypeOptions[] = [
  LINE_TYPE_OPTIONS.KNOWN_AND_UNKNOWN_SPARE_PART,
  LINE_TYPE_OPTIONS.UNKNOWN_SPARE_PART,
  LINE_TYPE_OPTIONS.STOCK_ITEM,
];

export interface KnownAndUnknownSparePartQuoteLineFieldValues {
  id: string;
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.SPARE_PART_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.SPARE_PART_BUYING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.SPARE_PART_SELLING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.SPARE_PART_SELLING_PRICE_INCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.SPARE_PART_REF]?: AutocompleteOptionForHookForm<SparePartCatalogItemWithPrice>;
  [QUOTE_LINE_FIELDS.SPARE_PART_SUPPLIER]?: AutocompleteOptionForHookForm<SparePartSupplier>;
}
export interface UnknownSparePartQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.UNKNOWN_SPARE_PART_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SPARE_PART_BUYING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SPARE_PART_SELLING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SPARE_PART_SELLING_PRICE_INCLUDED_VAT]?: string;
  unknownSparePartRef?: string;
}

export interface KnownAndUnknownServiceQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.SERVICE_DESIGNATION]?: AutocompleteOptionForHookForm<InternalService>;
  [QUOTE_LINE_FIELDS.SERVICE_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.SERVICE_PRICE_INCLUDED_VAT]?: string;
}
export interface UnknownServiceQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.UNKNOWN_SERVICE_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SERVICE_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SERVICE_PRICE_INCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.UNKNOWN_SERVICE_PRICE_INCLUDED_VAT]?: string;
}
export interface DiscountQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.DISCOUNT_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.DISCOUNT_PRICE]?: string;
}
export interface StockItemQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_ID]?: string;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_REF]?: string;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_SUPPLIER]?: AutocompleteOptionForHookForm<SparePartSupplier>;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_BUYING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_SELLING_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.STOCK_ITEM_SELLING_PRICE_INCLUDED_VAT]?: string;
  isInstalled?: boolean;
}

export interface AdministrativeFeesQuoteLineFieldValues {
  recipient?: StrictAutocompleteOptionForHookForm<RecipientType>;
  [QUOTE_LINE_FIELDS.LINE_TYPE]: AutocompleteOptionForHookForm;
  [QUOTE_LINE_FIELDS.ADMINISTRATIVE_FEES_DESIGNATION]?: string;
  [QUOTE_LINE_FIELDS.ADMINISTRATIVE_FEES_PRICE_EXCLUDED_VAT]?: string;
  [QUOTE_LINE_FIELDS.ADMINISTRATIVE_FEES_PRICE_INCLUDED_VAT]?: string;
}
export type QuoteLineFieldValues =
  | KnownAndUnknownSparePartQuoteLineFieldValues
  | UnknownSparePartQuoteLineFieldValues
  | DiscountQuoteLineFieldValues
  | KnownAndUnknownServiceQuoteLineFieldValues
  | UnknownServiceQuoteLineFieldValues
  | AdministrativeFeesQuoteLineFieldValues
  | StockItemQuoteLineFieldValues;

export interface QuoteFormLineFieldValues {
  line: QuoteLineFieldValues[];
}

export const isDiscountQuoteLineFieldValues = (
  object: any,
): object is DiscountQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.DISCOUNT
  );
};

export const isStockItemQuoteLineFieldValues = (
  object: any,
): object is StockItemQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.STOCK_ITEM
  );
};

export const isAdministrativeFeesQuoteLineFieldValues = (
  object: any,
): object is AdministrativeFeesQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.ADMINISTRATIVE_FEES
  );
};

export const isUnknownServiceQuoteLineFieldValues = (
  object: any,
): object is UnknownServiceQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.UNKNOWN_SERVICE
  );
};

export const isKnownAndUnknownServiceQuoteLineFieldValues = (
  object: any,
): object is KnownAndUnknownServiceQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.KNOWN_AND_UNKNOWN_SERVICE
  );
};

export const isKnownAndUnknownSparePartQuoteLineFieldValues = (
  object: any,
): object is KnownAndUnknownSparePartQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.KNOWN_AND_UNKNOWN_SPARE_PART
  );
};

export const isUnknownSparePartQuoteLineFieldValues = (
  object: any,
): object is UnknownSparePartQuoteLineFieldValues => {
  return (
    'lineType' in object &&
    object.lineType?.value === LINE_TYPE_OPTIONS.UNKNOWN_SPARE_PART
  );
};
